import { AnnotatePanelProps } from "../annotate";
import { Button, Separator, SheetHeader, SheetTitle } from "@mindstonehq/ui";
import React from "react";
import CommentRow from "./comment-row";
import CommentInput from "./comment-input";
import Blockquote, { HighlightBlockquote } from "./blockquote";
import { IHighlightCommon } from "~/typings/annotate/annotation";
import useCommentMutations from "~/components/annotate/hooks/comment-mutations";
import { Trans } from "@lingui/react/macro";

export interface CommentCommon {
  id: string;
  canEdit: boolean;
  createdAt: string;
  profile: {
    displayName: string;
    username: string;
    profilePictureURL: string;
  };
  content: string;
}

export default function HighlightCommentsPanel({
  article,
  close,
  annotations,
  selectedHighlight,
  setSelectedHighlight,
  refresh,
  startingText,
}: AnnotatePanelProps) {
  const [text, setText] = React.useState(startingText || "");
  const listRef = React.useRef<HTMLDivElement>(null);
  const { addComment, deleteComment, isLoading } = useCommentMutations();
  const comments = (annotations.highlights.find(
    (highlight) => highlight.id === selectedHighlight?.id,
  )?.comments || []) as Array<CommentCommon>;

  const scrollToBottom = () => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight + 500,
      behavior: "smooth",
    });
  };

  const handleDelete = (id: string) => {
    return deleteComment(id, article.id).then((res) => {
      refresh();
    });
  };

  const createComment = () => {
    return addComment(
      text,
      selectedHighlight as IHighlightCommon,
      article.id,
    ).then(({ commentID, highlight }) => {
      refresh();
      if (highlight.id !== selectedHighlight?.id) {
        setSelectedHighlight(highlight);
      }
      setText("");
      setTimeout(() => {
        scrollToBottom();
      }, 500);
      return commentID;
    });
  };

  return (
    <div className="h-full flex flex-col justify-between">
      <SheetHeader>
        <SheetTitle className="text-2xl"><Trans>Public comments</Trans></SheetTitle>
      </SheetHeader>
      <Separator className="mt-[6px] bg-highlight" />
      <HighlightBlockquote
        text={selectedHighlight?.text || ""}
        model={selectedHighlight?.model}
      />
      <div
        className="flex flex-col flex-1 overflow-auto space-y-3 mt-4 px-6"
        ref={listRef}
      >
        {comments.map((comment, i) => (
          <CommentRow key={i} comment={comment} handleDelete={handleDelete} />
        ))}
      </div>
      <div className="pt-6 px-6">
        <CommentInput
          text={text}
          setText={setText}
          createComment={createComment}
        />
      </div>
      <div className="px-6 self-end pb-6">
        <Button
          variant="secondary"
          className="w-auto"
          disabled={!text || isLoading}
          onClick={createComment}
        >
          <Trans>Post Comment</Trans>
        </Button>
      </div>
    </div>
  );
}
